/**
 * Normalize string for comparison (e.g. French: é, ê, è → e).
 * Uses NFD to decompose then strips combining diacritical marks.
 */
function normalizeForComparison(s) {
    if (!s || typeof s !== "string") return s;
    return s
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();
}

/**
 * Validates that each (row, col) in filledCoordinates has at most one value.
 * If the same cell is claimed by different entries with different values, returns conflicts.
 * Uses normalization for French (é, ê, è, etc. treated as same as e).
 * @param {Array} filledCoordinates - Parsed array of { positions: [{ row, col, value? }], text?, direction? }
 * @returns {{ valid: boolean, conflicts?: Array<{ row: number, col: number, values: string[] }> }}
 */
export function validateFilledCoordinatesCellConflicts(filledCoordinates) {
    if (!filledCoordinates || !Array.isArray(filledCoordinates)) {
        return { valid: true };
    }
    const cellToValues = new Map();
    for (const entry of filledCoordinates) {
        const positions = entry.positions || [];
        for (const pos of positions) {
            const row = pos.row;
            const col = pos.col;
            if (row === undefined || col === undefined) continue;
            const raw = pos.value != null && pos.value !== "" ? String(pos.value).trim() : "(empty)";
            const value = raw === "(empty)" ? raw : raw.toLowerCase();
            const normalized = raw === "(empty)" ? raw : normalizeForComparison(raw);
            const key = `${row},${col}`;
            if (!cellToValues.has(key)) {
                cellToValues.set(key, { normalized: new Set(), values: new Set() });
            }
            const cell = cellToValues.get(key);
            cell.normalized.add(normalized);
            cell.values.add(value);
        }
    }
    const conflicts = [];
    for (const [key, { normalized, values }] of cellToValues) {
        if (normalized.size > 1) {
            const [r, c] = key.split(",").map(Number);
            conflicts.push({ row: r, col: c, values: [...values] });
        }
    }
    return conflicts.length === 0 ? { valid: true } : { valid: false, conflicts };
}

/**
 * Normalize word for grid comparison: remove accents, hyphens, and spaces.
 * Hyphens (e.g. in "head-on") and spaces (e.g. in "dig over") are allowed in the word but not used in the grid.
 */
function normalizeWordForGrid(s) {
    if (!s || typeof s !== "string") return s;
    return normalizeForComparison(s).replace(/-/g, "").replace(/\s/g, "");
}

/**
 * Validates that each word entry's position values spell the word (entry.text).
 * Values must match the word in order or in reverse (for reversed words).
 * Uses normalization for French (é, ê, è, etc. treated as same as e).
 * Hyphens in the word (e.g. "head-on") are ignored since they are not used in the grid.
 * @param {Array} filledCoordinates - Parsed array of { positions: [{ row, col, value? }], text?, reversed? }
 * @returns {{ valid: boolean, mismatches?: Array<{ word: string, expected: string, actual: string }> }}
 */
export function validateWordPositionValues(filledCoordinates) {
    if (!filledCoordinates || !Array.isArray(filledCoordinates)) {
        return { valid: true };
    }
    const mismatches = [];
    for (const entry of filledCoordinates) {
        const word = (entry.text || entry.word || "").trim();
        const positions = entry.positions || [];
        if (!word || positions.length === 0) continue;
        const expectedForward = normalizeWordForGrid(word);
        const expectedReversed = expectedForward.split("").reverse().join("");
        const actualRaw = positions
            .map((p) => (p.value != null && p.value !== "" ? String(p.value).trim() : ""))
            .join("");
        const actualNormalized = normalizeForComparison(actualRaw);
        if (actualNormalized !== expectedForward && actualNormalized !== expectedReversed) {
            mismatches.push({
                word: entry.text || entry.word || word,
                expected: word,
                actual: positions.map((p) => (p.value != null ? String(p.value) : "")).join(","),
            });
        }
    }
    return mismatches.length === 0 ? { valid: true } : { valid: false, mismatches };
}
